import axios from 'axios';
import { User, UserManagementEvents, UserManager } from './user-management';
import { appconfig } from '../../appconfig';

export function createUserManager() {
    return new CookieUserManager();
}

class CookieUserManager implements UserManager {
    readonly events: UserManagementEvents = new CookieUserManagementEvents();
    private _user: User | null = null;
    private _flash: string | null | undefined = null;

    clearStaleState(): Promise<void> {
        return Promise.resolve(undefined);
    }

    async getUser(): Promise<User | null> {
        if (this._user) {
            return this._user;
        }

        try {
            const url = `${appconfig.API_URL}/session/user`;
            const response = await axios.get<GetUserDto>(url);
            this._user = response.data?.user ?? null;
            if (this._user) {
                this.events.load(this._user);
            }
            if (response.data?.flash) {
                this._flash = response.data.flash;
            }
            return this._user;
        } catch(error: unknown) {
            console.log("no user found");
            return null;
        }
    }

    getFlash(): string | undefined {
        const flash = this._flash ?? undefined;
        this._flash = null ;
        return flash;
    }

    removeUser(): Promise<void> {
        if (this._user) {
            this._user = null;
            this.events.unload();
        }
        return Promise.resolve(undefined);
    }

    revokeAccessToken(): Promise<void> {
        return this.removeUser();
    }

    signoutRedirect(msg?: string): Promise<void> {
        let url = "/session/logout";
        if (msg) {
            url += '?msg=' + encodeURIComponent(msg);
        }
        window.location.replace(url);
        return Promise.resolve(undefined);
    }
}

class CookieUserManagementEvents implements UserManagementEvents {
    private _userLoadedCallbacks: UserManagementEvents.UserLoadedCallback[] = [];
    private _userUnloadedCallbacks: UserManagementEvents.UserUnloadedCallback[] = [];
    private _silentRenewErrorCallbacks: UserManagementEvents.SilentRenewErrorCallback[] = [];

    load(user: User): void {
        for (let callback of this._userLoadedCallbacks) {
            callback(user);
        }
    }

    unload(): void {
        for (let callback of this._userUnloadedCallbacks) {
            callback();
        }
    }

    addUserLoaded(callback: UserManagementEvents.UserLoadedCallback): void {
        this._userLoadedCallbacks.push(callback);
    }

    addUserUnloaded(callback: UserManagementEvents.UserUnloadedCallback): void {
        this._userUnloadedCallbacks.push(callback);
    }

    removeUserLoaded(callback: UserManagementEvents.UserLoadedCallback): void {
        const index = this._userLoadedCallbacks.findIndex(cb => cb === callback);
        if (index >= 0) {
            this._userLoadedCallbacks.splice(index, 1);
        }
    }

    removeUserUnloaded(callback: UserManagementEvents.UserUnloadedCallback): void {
        const index = this._userUnloadedCallbacks.findIndex((cb) => cb === callback);
        if (index >= 0) {
            this._userUnloadedCallbacks.splice(index, 1);
        }
    }

    addSilentRenewError(callback: UserManagementEvents.SilentRenewErrorCallback): void {
        // does nothing
    }
}

type GetUserDto = {
    user?: User;
    flash?: string;
}